import { ensureOpenPencilSourceWorkspaceLoaded } from '@/features/openpencil-figma/openpencil-source-workspace-storage'
import { buildSourceTokenIndex } from '@/features/openpencil-figma/openpencil-source-library-model'
import { currentProjectSourceAuthority } from '@/mcp/register-cogo-xyz-tools'
import type { SourceTokenSnapshot } from './capability'

/**
 * Real `loadTokens` wiring for the `deriveFromSource` WebMCP capability —
 * reads the open project's OpenPencil source workspace.
 *
 * `ensureOpenPencilSourceWorkspaceLoaded()` resolving to `null` is
 * ambiguous by itself — it is what "this project never imported a source"
 * looks like, AND it is what "this project's source import failed to
 * (re)load just now" looks like, because the reads underneath it swallow
 * their own failures instead of rejecting:
 *   - `getProject()` (services/project-api.ts:2675-2681) catches every
 *     error and returns `null`.
 *   - `restoreOpenPencilSourceWorkspaceFromUrl()`
 *     (openpencil-source-workspace-storage.ts:197-211) returns
 *     `{ workspace: null, failure: result.reason }` on a failed fetch
 *     rather than throwing.
 * Collapsing both into "no source, 6 axes gap" would violate the one rule
 * this capability exists to keep: a source the project says it has must
 * never be silently dropped.
 *
 * `currentProjectSourceAuthority()` (register-cogo-xyz-tools.ts) is the
 * project-metadata marker that already exists for exactly this
 * disambiguation: `assertCurrentSourceGenerationAdmitted`/
 * `assertCurrentSourceReleaseAdmitted` in the same file use the identical
 * `!workspace && currentProjectSourceAuthority()` guard to fail closed
 * before export/release. Reused here rather than re-derived, so this
 * capability's notion of "source drift" never diverges from theirs.
 *
 * Boundary: when the marker itself cannot be read (`currentProject` is
 * missing/malformed — the same upstream swallow), `currentProjectSource
 * Authority()` already returns `null` for that reason too; there is no
 * separate "marker unreadable" signal to act on differently. That is
 * treated the same as "no source was ever registered" (gap, not thrown) —
 * by the time this capability is reachable at all, the Studio session
 * already depends on `currentProject` being loaded for far more than this
 * one tool, so a missing `currentProject` is a broken session upstream of
 * this tool, not a distinct case this tool can meaningfully act on.
 */
export async function loadCurrentProjectSourceTokens(): Promise<SourceTokenSnapshot> {
  const workspace = await ensureOpenPencilSourceWorkspaceLoaded()
  if (!workspace) {
    if (currentProjectSourceAuthority()) {
      throw new Error(
        'This project has a registered design source, but it could not be loaded just now. ' +
          'Retry once the source is reachable — this tool will not silently derive from nothing.',
      )
    }
    return { kind: 'figma', label: '', tokens: [] }
  }
  const index = buildSourceTokenIndex(workspace.bundle)
  return {
    kind: 'figma',
    label: workspace.bundle.fileName,
    tokens: index.groups.flatMap((group) => group.tokens),
  }
}
